// ============================================================================
// api_regieIntercomm.ts
// Gestion des PeerConnections intercomm côté régie.
//
// Pour chaque device connecté, la régie crée :
//   - Une PC "regie_to_device" (sendonly) : envoie le mic de la régie
//   - Une PC "device_to_regie" (recvonly) : reçoit le mic du device
//
// Toutes les PCs sont créées à la connexion du device.
// L'audio est muet par défaut, activé via setRegieIntercommActive().
// ============================================================================

import type { MutableRefObject } from 'react';
import { defaultRTCConfig } from '../../dataTypes/liveStreamManagment/utils_webrtc';
import { SdpLogger } from '../../dataTypes/liveStreamManagment/types_callbacks';
import { trackPeerConnection } from './api_webrtcBuffers';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RegieIntercommDevice {
  // Régie → Device
  pcSend       : RTCPeerConnection | null;
  audioSender  : RTCRtpSender | null;      // sender stocké pour éviter getSenders()
  micTrack     : MediaStreamTrack | null;  // piste mic originale (pour replaceTrack si clone ended)
  iceBufSend   : RTCIceCandidateInit[];
  // Device → Régie
  pcReceive     : RTCPeerConnection | null;
  iceBufReceive : RTCIceCandidateInit[];
  receiveStream : MediaStream | null;
}

export type RegieIntercommMap = Map<string, RegieIntercommDevice>;

export interface RegieIntercommCallbacks {
  addSdpLog: SdpLogger;
  // Appelé quand le stream audio d'un device devient disponible
  onDeviceReceiveStreamReady: (deviceId: string, stream: MediaStream) => void;
}

// ─── Helper WS ────────────────────────────────────────────────────────────────

const wsSend = (wsRef: MutableRefObject<WebSocket | null>, payload: object): void => {
  if (wsRef.current?.readyState === WebSocket.OPEN)
    wsRef.current.send(JSON.stringify(payload));
};

// ─── Helper : entrée vide ─────────────────────────────────────────────────────

const emptyEntry = (): RegieIntercommDevice => ({
  pcSend       : null,
  audioSender  : null,
  micTrack     : null,
  iceBufSend   : [],
  pcReceive    : null,
  iceBufReceive: [],
  receiveStream: null,
});

// ============================================================================
// SECTION A — Initialisation des deux PCs pour un device
// Appelée juste après la connexion du device (après auth_success + ready)
// ============================================================================

export const initRegieIntercommForDevice = async (
  deviceId        : string,
  micStream       : MediaStream | null,        // stream mic capturé dans LiveStreamManager
  intercommMapRef : MutableRefObject<RegieIntercommMap>,
  wsRef           : MutableRefObject<WebSocket | null>,
  callbacks       : RegieIntercommCallbacks,
): Promise<void> => {
  // Nettoyer une éventuelle entrée précédente
  closeRegieIntercommForDevice(deviceId, intercommMapRef, callbacks);

  const entry = emptyEntry();
  intercommMapRef.current.set(deviceId, entry);

  callbacks.addSdpLog({
    direction: 'info', type: 'event',
    label: `🎙 Intercomm init pour device ${deviceId}`,
  });

  // ── A1 : PC régie → device (sendonly) ─────────────────────────────────────
  {
    const pc = new RTCPeerConnection(defaultRTCConfig());
    trackPeerConnection(pc, 'intercom-envoi', deviceId);
    entry.pcSend = pc;

    // Ajouter le track mic (muet par défaut)
    if (micStream) {
      const audioTrack = micStream.getAudioTracks()[0];
      if (audioTrack) {
        const mutedTrack = audioTrack.clone();
        mutedTrack.enabled = false;
        const sender = pc.addTrack(mutedTrack, micStream);
        entry.audioSender = sender;
        entry.micTrack = audioTrack; // piste originale (pour replaceTrack si clone ended)
      }
    }

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        wsSend(wsRef, {
          type     : 'ice-candidate',
          target   : deviceId,
          stream_id: 'intercomm_regie_to_device',
          payload  : e.candidate,
        });
      }
    };

    pc.onconnectionstatechange = () => {
      callbacks.addSdpLog({
        direction: 'info', type: 'event',
        label: `🎙 Intercomm send→${deviceId}: ${pc.connectionState}`,
      });
      if (pc.connectionState === 'connected') {
        const statsInterval = setInterval(async () => {
          if (pc.connectionState !== 'connected') { clearInterval(statsInterval); return; }
          try {
            const stats = await pc.getStats();
            stats.forEach((report: any) => {
              if (report.type === 'outbound-rtp' && report.kind === 'audio') {
                const track = intercommMapRef.current.get(deviceId)?.audioSender?.track;
                const trackEnabled = track?.enabled ?? false;
                const trackState  = track?.readyState ?? 'n/a';
                const logMsg = `🎙📤 régie→${deviceId} packetsSent=${report.packetsSent ?? 0} bytesSent=${report.bytesSent ?? 0} trackEnabled=${trackEnabled} trackState=${trackState}`;
                callbacks.addSdpLog({ direction: 'info', type: 'event', label: logMsg });
                console.log(`[Intercomm] ${logMsg}`);
              }
            });
          } catch {}
        }, 5000);
      }
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        const e2 = intercommMapRef.current.get(deviceId);
        if (e2) e2.pcSend = null;
        // Oublier la référence ne ferme pas la connexion : sans ceci elle restait
        // ouverte, comptée parmi les tuyaux, et personne ne pouvait plus la
        // joindre pour la fermer.
        if (pc.connectionState === 'failed') { try { pc.close(); } catch { /* déjà fermée */ } }
      }
    };

    const offer = await pc.createOffer({ offerToReceiveAudio: false });
    await pc.setLocalDescription(offer);

    callbacks.addSdpLog({
      direction: 'sent', type: 'offer',
      label: `🎙 Intercomm send offer → ${deviceId}`,
      sdp: offer.sdp,
    });

    wsSend(wsRef, {
      type     : 'offer',
      target   : deviceId,
      stream_id: 'intercomm_regie_to_device',
      payload  : offer,
    });
  }

  // ── A2 : PC device → régie (recvonly) ─────────────────────────────────────
  {
    const pc = new RTCPeerConnection(defaultRTCConfig());
    trackPeerConnection(pc, 'intercom-retour', deviceId);
    entry.pcReceive = pc;

    pc.ontrack = (event) => {
      callbacks.addSdpLog({
        direction: 'received', type: 'event',
        label: `🎙 Intercomm receive: track de ${deviceId} reçu (${event.track.kind})`,
      });
      if (event.streams[0]) {
        entry.receiveStream = event.streams[0];
        callbacks.onDeviceReceiveStreamReady(deviceId, event.streams[0]);
      }
    };

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        wsSend(wsRef, {
          type     : 'ice-candidate',
          target   : deviceId,
          stream_id: 'intercomm_device_to_regie',
          payload  : e.candidate,
        });
      }
    };

    pc.onconnectionstatechange = () => {
      callbacks.addSdpLog({
        direction: 'info', type: 'event',
        label: `🎙 Intercomm receive←${deviceId}: ${pc.connectionState}`,
      });
      if (pc.connectionState === 'connected') {
        const statsInterval = setInterval(async () => {
          if (pc.connectionState !== 'connected') { clearInterval(statsInterval); return; }
          try {
            const stats = await pc.getStats();
            stats.forEach((report: any) => {
              if (report.type === 'inbound-rtp' && report.kind === 'audio') {
                callbacks.addSdpLog({
                  direction: 'info', type: 'event',
                  label: `🎙📥 régie←${deviceId} audioLevel=${(report.audioLevel ?? 0).toFixed(4)} packetsReceived=${report.packetsReceived ?? 0} totalSamples=${report.totalSamplesReceived ?? 0}`,
                });
              }
            });
          } catch {}
        }, 5000);
      }
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        const e2 = intercommMapRef.current.get(deviceId);
        if (e2) e2.pcReceive = null;
        // Même raison que pour le sens envoi : oublier la référence ne ferme rien.
        if (pc.connectionState === 'failed') { try { pc.close(); } catch { /* déjà fermée */ } }
      }
    };

    const offer = await pc.createOffer({ offerToReceiveAudio: true });
    await pc.setLocalDescription(offer);

    callbacks.addSdpLog({
      direction: 'sent', type: 'offer',
      label: `🎙 Intercomm receive offer → ${deviceId}`,
      sdp: offer.sdp,
    });

    wsSend(wsRef, {
      type     : 'offer',
      target   : deviceId,
      stream_id: 'intercomm_device_to_regie',
      payload  : offer,
    });
  }
};

// ============================================================================
// SECTION B — Réception des answers du device
// ============================================================================

export const handleRegieIntercommAnswer = async (
  deviceId        : string,
  streamId        : string, // 'intercomm_regie_to_device' ou 'intercomm_device_to_regie'
  answer          : RTCSessionDescriptionInit,
  intercommMapRef : MutableRefObject<RegieIntercommMap>,
  callbacks       : Pick<RegieIntercommCallbacks, 'addSdpLog'>,
): Promise<void> => {
  const entry = intercommMapRef.current.get(deviceId);
  if (!entry) return;

  const isReceive = streamId === 'intercomm_device_to_regie';
  const pc = isReceive ? entry.pcReceive : entry.pcSend;
  if (!pc) return;

  callbacks.addSdpLog({
    direction: 'received', type: 'answer',
    label: `🎙 Intercomm answer de ${deviceId} (${streamId})`,
    sdp: answer.sdp,
  });

  await pc.setRemoteDescription(answer);

  // Flush ICE buffer
  const buf = isReceive ? entry.iceBufReceive : entry.iceBufSend;
  const candidates = buf.splice(0);
  for (const c of candidates) {
    try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch {}
  }
};

// ============================================================================
// SECTION C — ICE candidates entrants depuis le device
// ============================================================================

export const handleRegieIntercommIce = async (
  deviceId        : string,
  streamId        : string,
  candidate       : RTCIceCandidateInit,
  intercommMapRef : MutableRefObject<RegieIntercommMap>,
): Promise<void> => {
  const entry = intercommMapRef.current.get(deviceId);
  if (!entry) return;

  const isReceive = streamId === 'intercomm_device_to_regie';
  const pc  = isReceive ? entry.pcReceive  : entry.pcSend;
  const buf = isReceive ? entry.iceBufReceive : entry.iceBufSend;

  if (!pc) {
    buf.push(candidate);
    return;
  }
  try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch {}
};

// ============================================================================
// SECTION D — Activation / désactivation de l'intercomm régie → devices
// Seuls les devices dans `selectedDeviceIds` reçoivent le son.
// Le gain retour est abaissé via un message WS custom.
// ============================================================================

export const setRegieIntercommActive = (
  active           : boolean,
  selectedDeviceIds: Set<string>,
  intercommMapRef  : MutableRefObject<RegieIntercommMap>,
  wsRef            : MutableRefObject<WebSocket | null>,
  callbacks        : Pick<RegieIntercommCallbacks, 'addSdpLog'>,
): void => {
  console.log(`[Intercomm] setRegieIntercommActive active=${active} selected=[${Array.from(selectedDeviceIds).join(',')}] mapSize=${intercommMapRef.current.size}`);

  intercommMapRef.current.forEach((entry, deviceId) => {
    if (!entry.pcSend) {
      console.warn(`[Intercomm] ${deviceId}: pcSend=null, skip`);
      return;
    }

    const isSelected     = selectedDeviceIds.has(deviceId);
    const shouldBeActive = active && isSelected;
    const pcState        = entry.pcSend.connectionState;
    const sender         = entry.audioSender;
    const track          = sender?.track ?? null;

    console.log(`[Intercomm] ${deviceId}: isSelected=${isSelected} shouldBeActive=${shouldBeActive} pcState=${pcState} sender=${sender ? 'ok' : 'null'} track=${track ? `kind=${track.kind} enabled=${track.enabled} readyState=${track.readyState}` : 'null'}`);

    if (sender) {
      if (!track || track.readyState === 'ended') {
        // Piste terminée ou absente → recréer un clone frais depuis la piste mic originale
        const micTrack = entry.micTrack;
        if (micTrack && micTrack.readyState === 'live') {
          const freshClone = micTrack.clone();
          freshClone.enabled = shouldBeActive;
          sender.replaceTrack(freshClone)
            .then(() => console.log(`[Intercomm] ${deviceId}: replaceTrack (clone frais) → enabled=${shouldBeActive}`))
            .catch(e  => console.warn(`[Intercomm] ${deviceId}: replaceTrack échoué`, String(e)));
        } else {
          console.warn(`[Intercomm] ${deviceId}: track ended ET micTrack indisponible (readyState=${micTrack?.readyState ?? 'null'})`);
        }
      } else {
        // Piste vivante — simple toggle enabled
        track.enabled = shouldBeActive;
        console.log(`[Intercomm] ${deviceId}: track.enabled → ${shouldBeActive} (readyState=${track.readyState})`);
      }
    }

    // Notifier le device : il en tire son ducking de retour ET l'ouverture de
    // son micro. Envoyé à TOUS les devices, y compris non sélectionnés : c'est le
    // seul message qui referme un micro, et désélectionner une caméra alors que
    // l'intercomm est actif ne passe que par ici. Ne l'émettre que pour les
    // sélectionnés laissait le micro du device écarté ouvert indéfiniment.
    wsSend(wsRef, {
      type  : 'intercomm',
      target: deviceId,
      active: shouldBeActive,
    });

    callbacks.addSdpLog({
      direction: 'sent', type: 'event',
      label: `🎙 Intercomm ${shouldBeActive ? 'ON' : 'OFF'} → ${deviceId}`,
    });
  });
};

// ============================================================================
// SECTION E — Fermeture pour un device
// ============================================================================

export const closeRegieIntercommForDevice = (
  deviceId        : string,
  intercommMapRef : MutableRefObject<RegieIntercommMap>,
  callbacks       : Pick<RegieIntercommCallbacks, 'addSdpLog'>,
): void => {
  const entry = intercommMapRef.current.get(deviceId);
  if (!entry) return;

  // La copie du micro envoyée à ce device lui est propre (clone() à
  // l'ouverture, ou clone frais après une piste terminée) : l'arrêter, sinon
  // elle restait vivante après la fermeture de la PC — une de plus, et autant
  // de traitement audio, à chaque reconnexion d'un device. Jamais la piste
  // d'origine (entry.micTrack) : elle est partagée par tous les devices.
  const sentTrack = entry.audioSender?.track ?? null;
  if (sentTrack && sentTrack !== entry.micTrack) {
    sentTrack.stop();
  }

  if (entry.pcSend) {
    entry.pcSend.close();
    entry.pcSend = null;
  }
  if (entry.pcReceive) {
    entry.pcReceive.close();
    entry.pcReceive = null;
  }
  intercommMapRef.current.delete(deviceId);

  callbacks.addSdpLog({
    direction: 'info', type: 'event',
    label: `🎙 Intercomm PCs fermées pour ${deviceId}`,
  });
};

// ============================================================================
// SECTION F — Fermeture complète (déconnexion régie)
// ============================================================================

export const closeAllRegieIntercomm = (
  intercommMapRef : MutableRefObject<RegieIntercommMap>,
  callbacks       : Pick<RegieIntercommCallbacks, 'addSdpLog'>,
): void => {
  intercommMapRef.current.forEach((_, deviceId) => {
    closeRegieIntercommForDevice(deviceId, intercommMapRef, callbacks);
  });
  intercommMapRef.current.clear();
};